/// «Абонементи» — every word of the section, in its own file so that it
/// merges cleanly next to other sections (uk.ts carries one line for it).
/// Sentence case; plurals through `plural`.

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

export const days = (n: number) => `${n} ${plural(n, 'день', 'дні', 'днів')}`
export const months = (n: number) => `${n} ${plural(n, 'місяць', 'місяці', 'місяців')}`
export const years = (n: number) => `${n} ${plural(n, 'рік', 'роки', 'років')}`
/// After «від» and «до»: «від 1 року», «до 14 років».
const yearsFrom = (n: number) => `${n} ${plural(n, 'року', 'років', 'років')}`
export const visits = (n: number) => `${n} ${plural(n, 'візит', 'візити', 'візитів')}`
export const times = (n: number) => `${n} ${plural(n, 'раз', 'рази', 'разів')}`

export const passesUk = {
  nav: 'Мої продукти',
  title: 'Мої продукти',
  subtitle: 'Продані абонементи й тарифи, які продає клуб.',
  tabs: { sold: 'Абонементи', plans: 'Тарифи' },
  /// The client card's section — the client's own passes.
  clientSection: 'Абонементи',

  plans: {
    subtitle: 'Що продає клуб. Кожен тариф складається з блоків — правил, які перевіряють на вході.',
    add: 'Новий тариф',
    onSale: 'У продажу',
    retired: 'Зняті з продажу',
    retiredFootnote: 'Зняті тарифи не продаються, але продані за ними абонементи діють за своїми умовами.',
    empty: 'Ще немає жодного тарифу',
    emptyMessage: 'Зберіть перший у конструкторі — з готового шаблону це хвилина.',
    emptyReadOnly: 'Тарифи створює власник або директор клубу.',
    readOnly: 'Ви бачите тарифи, але змінювати їх може роль із правом «Абонементи: керування».',
    allBranches: 'Усі клуби',
    open: 'Відкрити',
    edit: 'Змінити',
    duplicate: 'Дублювати',
    retire: 'Зняти з продажу',
    restore: 'Повернути в продаж',
    retireTitle: (name: string) => `Зняти «${name}» з продажу?`,
    retireMessage:
      'Його більше не можна буде продати. Уже продані абонементи працюють далі за своїми умовами. Тариф можна повернути в продаж.',
    retireConfirm: 'Зняти з продажу',
    retiredBanner: (name: string) => `«${name}» знято з продажу.`,
    delete: 'Видалити',
    deleteTitle: (name: string) => `Видалити «${name}» назавжди?`,
    deleteMessage:
      'Тариф зникне зі списку, і повернути його не вийде. Уже продані за ним абонементи лишаються й діють за своїми умовами. Якщо тариф може знадобитися — краще зняти його з продажу.',
    deleteConfirm: 'Видалити назавжди',
    deletedBanner: (name: string) => `«${name}» видалено.`,
    restoredBanner: (name: string) => `«${name}» знову в продажу.`,
    copyName: (name: string) => `${name} (копія)`,
    oldFormat: 'Створено до конструктора',
    columns: { name: 'Тариф', rules: 'Умови', branch: 'Де продається', price: 'Ціна' },
  },

  builder: {
    newTitle: 'Новий тариф',
    back: 'Тарифи',
    create: 'Створити тариф',
    save: 'Зберегти тариф',
    changedNotSaved: 'Є незбережені зміни',
    allSaved: 'Усі зміни збережено',
    notCreated: 'Тариф ще не створено',
    discard: 'Скасувати зміни',
    saveBar: 'Збереження тарифу',
    savedNote: 'Збережено. Уже продані абонементи лишаються за старими умовами.',
    readOnly: 'Ви бачите правила цього тарифу, але змінити їх не можете.',
    retiredNote: 'Тариф знято з продажу. Його можна редагувати, але продати — лише після повернення в продаж.',
    basics: 'Основне',
    name: 'Назва',
    namePlaceholder: 'Наприклад, «Безліміт на місяць»',
    nameRequired: 'Назвіть тариф',
    price: 'Ціна',
    pricePlaceholder: '0',
    priceInvalid: 'Ціна — це число гривень, наприклад 1200 або 1200,50',
    description: 'Опис для адміністратора',
    descriptionPlaceholder: 'Необовʼязково',
    branch: 'Де продається',
    branchAll: 'В усіх клубах',
    branchAllHint: 'Мережевий тариф може створити лише роль з охопленням «Уся компанія».',
    palette: 'Блоки',
    paletteHint: 'Перетягніть блок на тариф або натисніть, щоб додати.',
    paletteAddTo: (where: string) => `Додаємо в: ${where}`,
    planLevel: 'Про абонемент',
    rules: 'Хто й коли проходить',
    groupsTitle: 'Групи',
    canvas: 'Правила тарифу',
    canvasEmpty: 'Перетягніть сюди блоки з лівої панелі або почніть із шаблону.',
    canvasEmptyReadOnly: 'У цього тарифу немає правил.',
    topLevel: 'весь абонемент',
    groupHint:
      'Група — окремий доступ зі своїми правилами й лічильником. Абонемент пропускає, якщо підходить хоча б одна група.',
    groupLabel: 'Назва групи',
    groupLabelPlaceholder: 'Наприклад, «Басейн»',
    groupLabelRequired: 'Назвіть групу',
    groupEmpty: 'Перетягніть сюди правила для цієї групи — зони, години, візити.',
    groupNew: (n: number) => `Доступ ${n}`,
    addRule: 'Додати правило',
    addBlock: 'Додати блок',
    moveUp: 'Вище',
    moveDown: 'Нижче',
    moveInto: (group: string) => `Перенести в «${group}»`,
    moveOut: 'Винести з групи',
    remove: 'Прибрати',
    dragHandle: (block: string) => `Перетягнути: ${block}`,
    collapse: 'Згорнути',
    expand: 'Розгорнути',
    blockActions: (block: string) => `Дії: ${block}`,
    sharedNote: 'Ці правила діють для всього абонемента, у кожній групі.',
    refusal: {
      planOnly: 'Цей блок стосується всього абонемента — його не можна класти в групу',
      noNestedGroups: 'Групу не можна вкласти в іншу групу',
      duplicate: 'Такий блок тут уже є — змініть його',
      tooManyGroups: 'Не більше 10 груп',
      noSuchGroup: 'Такої групи вже немає',
    },
    dnd: {
      instructions:
        'Щоб підняти блок, натисніть пробіл або Enter. Стрілками пересувайте, пробілом або Enter — покладіть, Escape — скасувати.',
      picked: (name: string) => `Піднято блок «${name}».`,
      over: (name: string, where: string) => `Блок «${name}» над: ${where}.`,
      dropped: (name: string, where: string) => `Блок «${name}» покладено: ${where}.`,
      cancelled: (name: string) => `Перенесення «${name}» скасовано.`,
      nowhere: 'поза тарифом',
    },
    templates: 'Почати з шаблону',
    templatesHint: 'Шаблон — це лише початок: будь-який блок можна змінити, прибрати чи додати.',
    blank: 'Порожній тариф',
    blankHint: 'Зібрати з нуля',
    preview: 'Як це виглядає',
    summaryEmpty: 'Додайте блоки — тут зʼявиться опис тарифу.',
    problems: 'Що виправити',
    unbounded:
      'Тариф без меж: додайте строк дії, кількість візитів для всього абонемента або візити в кожну групу.',
    classVisitNeedsPack:
      '«Заняття — окреме відвідування» списує візит із пакета: додайте візити для всього абонемента або в групу.',
  },

  blocks: {
    validity: { title: 'Строк дії', hint: 'Скільки днів чи місяців діє' },
    start: { title: 'Початок', hint: 'Коли починає діяти' },
    freeze: { title: 'Заморозка', hint: 'Пауза абонемента' },
    stay: { title: 'Тривалість візиту', hint: 'Скільки бути в клубі' },
    guests: { title: 'Гості', hint: 'Візити з гостем' },
    eligibility: { title: 'Лише для нових', hint: 'Для тих, хто ще не мав' },
    discounts: { title: 'Без знижок', hint: 'Лише за ціною тарифу' },
    age: { title: 'Вік', hint: 'Для якого віку' },
    included: { title: 'Входить у вартість', hint: 'Рушник, шафка тощо' },
    requiresPass: { title: 'Потрібен чинний абонемент', hint: 'Лише разом з іншим' },
    classVisit: { title: 'Заняття — окреме відвідування', hint: 'Зала й заняття — 2 візити' },
    zones: { title: 'Зони', hint: 'Куди пускає' },
    hours: { title: 'Дні й години', hint: 'Коли пускає' },
    visits: { title: 'Візити', hint: 'Скільки входів' },
    limits: { title: 'Ліміти', hint: 'Не частіше ніж' },
    trainer: { title: 'Лише з тренером', hint: 'Вхід лише на заняття' },
    group: { title: 'Група доступу', hint: 'Свій доступ і лічильник' },
  } as Record<string, { title: string; hint: string }>,

  editors: {
    days: 'Днів',
    months: 'Місяців',
    termUnit: 'Строк у днях чи місяцях',
    unitDays: 'Дні',
    unitMonths: 'Місяці',
    monthsHint: 'Місяць закінчується напередодні того самого числа: з 15 січня — по 14 лютого. З 31 січня — по кінець лютого.',
    unlimitedDays: 'Без строку',
    notAfter: 'Не пізніше',
    notAfterHint: 'Абонемент перестає діяти цього дня, навіть якщо дні ще лишилися.',
    clear: 'Прибрати',
    start: {
      on_purchase: 'З дня продажу',
      on_first_visit: 'З першого візиту',
      fixed_date: 'З обраної дати',
      after_previous: 'Після попереднього абонемента',
    } as Record<string, string>,
    startDate: 'Дата початку',
    startDateHint: 'Порожньо — дату обирають при продажу.',
    freezeMaxDays: 'Разом днів',
    freezeMinDays: 'Мінімум за раз',
    freezeMaxCount: 'Скільки разів',
    freezePaid: 'Платна заморозка',
    minutes: 'Хвилин',
    guests: 'Гостьових візитів',
    eligibility: 'Купити може лише той, хто ще не мав жодного абонемента в цій компанії.',
    discounts: 'Продається лише за ціною тарифу або дорожче: швидкої знижки −10 % у вікні продажу не буде.',
    minAge: 'Від, років',
    maxAge: 'До, років',
    noMinAge: 'Без нижньої межі',
    noMaxAge: 'Без верхньої межі',
    ageHint: 'Обидві межі включно: «до 14» — чотирнадцятирічному продати можна, пʼятнадцятирічному — ні. Вік рахуємо на день продажу за датою народження з картки клієнта.',
    ageRequired: 'Вкажіть хоча б одну межу віку',
    ageMinOverMax: 'Нижня межа віку більша за верхню',
    includedPlaceholder: 'Що входить — і Enter',
    includedAdd: 'Додати',
    includedRequired: 'Додайте хоча б одну послугу',
    includedRemove: (item: string) => `Прибрати «${item}»`,
    includedHint: 'Лише для інформації: показуємо в описі тарифу й на проданому абонементі.',
    requiresPass:
      'Вхід лише тоді, коли в клієнта є інший чинний абонемент, наприклад у зал. Для пакетів тренувань, які не включають вхід.',
    classVisit:
      'Вхід через рецепцію списує один візит, а відмітка «Прийшов» на груповому занятті — ще один. Це 2 візити й тоді, коли клієнт прийшов лише на заняття, без зали: хто ходить тільки на групові, витрачає по 2 за кожне. Якщо візитів уже не лишилось, заняття записуємо в історію абонемента як борг, нічого не списуючи.',
    zonesPlaceholder: 'Нова зона й Enter',
    zonesAdd: 'Додати',
    zonesSuggested: 'Зони клубу',
    zonesRequired: 'Оберіть хоча б одну зону',
    zoneRemove: (zone: string) => `Прибрати зону «${zone}»`,
    windows: 'Вікна',
    addWindow: 'Додати вікно',
    removeWindow: 'Прибрати вікно',
    weekdays: 'Дні',
    everyDay: 'Щодня',
    workdays: 'Будні',
    weekend: 'Вихідні',
    from: 'З',
    to: 'До',
    allDay: 'Весь день',
    overnight: (to: string) => `До ${to} наступного ранку`,
    sameTime: 'Початок і кінець однакові',
    badTime: 'Час у форматі 07:00',
    noWindows: 'Додайте хоча б одне вікно',
    count: 'Кількість',
    perDay: 'На день',
    perWeek: 'На тиждень',
    perMonth: 'На місяць',
    limitsRequired: 'Увімкніть хоча б один ліміт',
    trainer: 'Пропускає лише на заняття з тренером (при записі чи на вході це позначає адміністратор).',
    decrease: 'Менше',
    increase: 'Більше',
    outOfRange: (min: number, max: number) => `Від ${min} до ${max}`,
    freezeMinOverMax: 'Мінімум за раз більший за загальну кількість днів',
    validityRequired: 'Вкажіть кількість днів або дату',
    badDate: 'Дата у форматі РРРР-ММ-ДД',
  },

  summary: {
    days,
    visits,
    unlimited: 'безліміт',
    notAfter: (date: string) => `до ${date}`,
    daysUntil: (n: number, date: string) => `${days(n)}, не пізніше ${date}`,
    months,
    monthsUntil: (n: number, date: string) => `${months(n)}, не пізніше ${date}`,
    fromFirstVisit: 'з першого візиту',
    fromDate: (date: string) => `з ${date}`,
    fromSale: 'з дати вибору при продажу',
    afterPrevious: 'після попереднього',
    everyDay: 'щодня',
    workdays: 'будні',
    weekend: 'вихідні',
    allDay: 'весь день',
    fromTime: (time: string) => `з ${time}`,
    untilTime: (time: string) => `до ${time}`,
    night: (from: string, to: string) => `${from}–${to} наступного ранку`,
    perDay: (n: number) => `${times(n)} на день`,
    perWeek: (n: number) => `${times(n)} на тиждень`,
    perMonth: (n: number) => `${times(n)} на місяць`,
    withTrainer: 'лише з тренером',
    stay: (n: number) => `до ${n} хв`,
    guests: (n: number) => `${n} ${plural(n, 'гостьовий', 'гостьові', 'гостьових')}`,
    freeze: (n: number) => `заморозка до ${days(n)}`,
    newOnly: 'лише для нових',
    noDiscounts: 'без знижок',
    age: (min: number | null, max: number | null) =>
      min != null && max != null
        ? min === max
          ? `вік ${years(min)}`
          : `вік від ${min} до ${yearsFrom(max)} включно`
        : max != null
          ? `вік до ${yearsFrom(max)} включно`
          : `вік від ${yearsFrom(min ?? 0)}`,
    included: (items: readonly string[]) => `входить: ${items.join(', ')}`,
    requiresPass: 'лише з іншим чинним абонементом',
    classVisit: 'групове заняття — ще один візит, навіть без зали',
    shortDays: ['нд', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'],
  },

  tester: {
    title: 'Перевірити',
    hint: 'Оберіть день, час і зону — і побачите, що скаже вхід. Перевіряє той самий сервер, що й турнікет.',
    date: 'День',
    time: 'Час',
    zone: 'Зона',
    anyZone: 'Не вказано',
    withTrainer: 'З тренером',
    holdsOtherPass: 'Є інший чинний абонемент',
    usage: 'Вже використано',
    today: 'Сьогодні',
    thisWeek: 'Цього тижня',
    thisMonth: 'Цього місяця',
    left: 'Лишилось візитів',
    groupLeft: (group: string) => `Лишилось у «${group}»`,
    inactive: 'Абонемент не активний',
    admits: 'Пропустить',
    refuses: 'Не пропустить',
    spends: 'Спише візит',
    free: 'Візит не списується',
    via: (group: string) => `за групою «${group}»`,
    fixFirst: 'Виправте правила вище — тоді можна перевіряти.',
    checking: 'Перевіряємо…',
    timeZone: (zone: string) => `Час клубу: ${zone}`,
  },

  reasons: {
    pass_inactive: 'Абонемент недійсний',
    other_pass_required: 'Потрібен інший чинний абонемент',
    zone_not_covered: 'Не відкриває цю зону',
    weekday_not_allowed: 'Не діє в цей день тижня',
    outside_day_window: 'Діє в інший час доби',
    trainer_required: 'У цей час — лише з тренером',
    per_day_limit: 'Ліміт на сьогодні вичерпано',
    per_week_limit: 'Ліміт на цей тиждень вичерпано',
    per_month_limit: 'Ліміт на цей місяць вичерпано',
    no_visits_left: 'Візити закінчились',
  } as Record<string, string>,

  templates: {
    unlimitedMonth: { name: 'Безліміт на місяць', hint: '30 днів, будь-коли, заморозка до 30 днів' },
    twelveVisits: { name: '12 занять', hint: '12 візитів за 2 місяці' },
    morning: { name: 'Ранковий', hint: 'Будні 7:00–16:00, 30 днів' },
    gymPool: { name: 'Басейн + зал', hint: 'Зал безліміт і 8 візитів у басейн' },
    weekdaysWeekend: { name: 'Будні вдень + вихідні', hint: 'Будні 7–16 і вихідні весь день' },
    trainerEvening: {
      name: 'Двічі на тиждень',
      hint: 'Не більше 1 на день, після 21:00 — з тренером',
    },
    firstMonth: { name: 'Перший місяць', hint: 'Знижка для тих, хто прийшов уперше' },
    untilYearEnd: { name: 'До кінця року', hint: '30 днів від першого візиту, не пізніше 31 грудня' },
    kids: { name: 'Дитячий', hint: '1 місяць, вік до 14 років включно' },
    trainerPack: { name: 'Персональні 8', hint: '8 занять з тренером, лише разом з абонементом у зал' },
    zoneGym: 'Тренажерний зал',
    zonePool: 'Басейн',
    groupGym: 'Тренажерний зал',
    groupPool: 'Басейн',
    groupDay: 'Зал до 21:00',
    groupLate: 'Зал після 21:00',
  },

  sold: {
    subtitle: 'Абонементи клієнтів: стан, візити, заморозки й продовження.',
    sell: 'Продати абонемент',
    empty: 'Ще не продано жодного абонемента',
    emptyMessage: 'Продайте перший — оберіть клієнта й тариф.',
    emptyFilter: 'Немає абонементів у цьому стані',
    filters: {
      all: 'Усі',
      active: 'Діють',
      pending: 'Чекають старту',
      frozen: 'Заморожені',
      expired: 'Завершені',
      revoked: 'Анульовані',
    } as Record<string, string>,
    status: {
      active: 'Діє',
      pending: 'Чекає старту',
      frozen: 'Заморожено',
      expired: 'Завершився',
      revoked: 'Анульовано',
    } as Record<string, string>,
    until: (date: string) => `до ${date}`,
    from: (date: string) => `з ${date}`,
    firstVisit: 'стартує з першого візиту',
    visitsOf: (left: number, total: number) => `${left} з ${total}`,
    unknownClient: 'Клієнт',
    ownScope: 'Ваша роль бачить лише своє — проданих абонементів вона не бачить.',
    shown: (n: number, total: number) => `Показано ${n} з ${total}`,
    loadMore: (n: number) => `Показати ще ${n}`,
    loadingMore: 'Завантаження…',
    columns: { client: 'Клієнт', plan: 'Тариф', dates: 'Строк', visits: 'Залишок', status: 'Стан' },
    unlimited: 'безліміт',
    filterLabel: 'Стан абонемента',
    soldBanner: (client: string, plan: string) => `Продано: ${plan} — ${client}.`,
  },

  pass: {
    plan: 'Тариф',
    status: 'Стан',
    starts: 'Початок',
    ends: 'Кінець',
    visits: 'Візити',
    guests: 'Гостьові',
    /// Next to the «Заморожено» badge — the badge already says the word.
    frozen: (from: string, to: string) => `${from} — ${to}`,
    frozenTotal: (n: number, count: number) => `Заморожувався ${times(count)}, разом ${days(n)}`,
    price: 'Ціна продажу',
    rules: 'Умови',
    rulesNote: 'Умови скопійовано в день продажу. Зміни тарифу їх не торкаються.',
    groups: 'Лічильники груп',
    unlimited: 'безліміт',
    notStarted: 'Ще не почався',
    noEnd: 'Без строку',
    /// Next to the «Анульовано» badge: only the reason, when there is one.
    revoked: (reason: string | null) => (reason ? `Причина: ${reason}` : ''),
    actions: 'Дії',
    freeze: 'Заморозити',
    unfreeze: 'Розморозити',
    extend: 'Продовжити',
    transfer: 'Передати іншому',
    revoke: 'Анулювати',
    history: 'Історія',
    noHistory: 'Поки нічого не відбувалося.',
    freezeFrom: 'З',
    freezeTo: 'По',
    freezeNote: 'Примітка',
    freezePolicy: (min: number, max: number, left: number, count: number) =>
      `Від ${days(min)} за раз; лишилось ${days(left)} і ${times(count)} із ${max} днів.`,
    noFreeze: 'Тариф цього абонемента не дозволяє заморозку.',
    freezeSubmit: 'Заморозити',
    unfreezeConfirm: 'Розморозити зараз? Абонемент подовжиться на дні, що вже минули в заморозці.',
    extendDays: 'Додати днів',
    extendVisits: 'Додати візитів',
    extendGroup: 'Куди додати візити',
    extendShared: 'Спільні візити',
    extendReason: 'Причина',
    extendReasonPlaceholder: 'Наприклад, «компенсація за ремонт»',
    extendEmpty: 'Додайте дні, візити або і те, і те',
    extendSubmit: 'Продовжити',
    transferTo: 'Новий власник',
    transferSubmit: 'Передати',
    transferSame: 'Абонемент уже в цього клієнта',
    revokeTitle: 'Анулювати абонемент?',
    revokeMessage: 'Це не можна скасувати: абонемент перестане діяти. Гроші тут не повертаються — це робить каса.',
    revokeReason: 'Причина',
    revokeConfirm: 'Анулювати',
    done: {
      frozen: 'Заморожено',
      unfrozen: 'Розморожено',
      extended: 'Продовжено',
      transferred: 'Передано',
      revoked: 'Анульовано',
    } as Record<string, string>,
    events: {
      issued: 'Продано',
      activated: 'Почав діяти',
      visit: 'Візит',
      frozen: 'Заморожено',
      unfrozen: 'Розморожено',
      extended: 'Продовжено',
      transferred: 'Передано',
      revoked: 'Анульовано',
      expired: 'Завершився',
    } as Record<string, string>,
    visitSpent: 'списано візит',
    classVisit: 'групове заняття',
    /// Why a class visit spent nothing (services/passes `shortfall`).
    shortfall: {
      no_visits_left: 'візитів не лишилось — не списано',
      pass_inactive: 'абонемент анульовано — не списано',
      zone_not_covered: 'зона не входить в абонемент — не списано',
      zone_unknown: 'у заняття не вказано зону, а груп із візитами кілька — не списано',
    } as Record<string, string>,
    guest: 'гість',
  },

  sell: {
    title: 'Продати абонемент',
    client: 'Клієнт',
    clientSearch: 'Імʼя, телефон, пошта або картка',
    clientNone: 'Нікого не знайдено. Додайте клієнта в розділі «Клієнти».',
    clientHint: 'Почніть вводити — шукаємо серед клієнтів компанії.',
    clientChange: 'Змінити',
    plan: 'Тариф',
    noPlans: 'Немає тарифів у продажу. Спочатку створіть тариф.',
    startsOn: 'Дата початку',
    startsOnHint: 'Тариф діє з обраної дати.',
    startRuleNote: {
      on_purchase: 'Почне діяти сьогодні.',
      on_first_visit: 'Почне діяти з першого візиту.',
      after_previous: 'Почне діяти після поточного абонемента клієнта.',
    } as Record<string, string>,
    noMoney: 'Гроші приймаєте як завжди — готівкою чи терміналом, eatlete їх не проводить. Тут записуємо абонемент і ціну, за яку його продано.',
    /// The panel's line under the title when the client is already chosen.
    forClient: (name: string) => `Клієнт: ${name}`,
    /// …and when it opened by itself right after «Новий клієнт».
    afterRegistration: (name: string) => `Клієнта «${name}» додано. Продайте абонемент зараз або пропустіть.`,
    skip: 'Пропустити',
    price: 'Ціна продажу',
    priceList: 'Ціна тарифу. Змініть, якщо даєте знижку.',
    priceListChip: 'За тарифом',
    priceOff: (off: string, percent: number, list: string) =>
      `Знижка ${off} (${String(percent).replace('.', ',')} %) від ціни тарифу ${list}`,
    priceAbove: (list: string) => `Більше за ціну тарифу ${list}`,
    priceInvalid: 'Вкажіть ціну числом, наприклад 945 або 945,50',
    noDiscounts: (list: string) => `Тариф без знижок: не дешевше за ${list}.`,
    priceBelowList: (list: string) => `Цей тариф продається без знижок — ціна не може бути нижчою за ${list}`,
    /// Under a plan with an age limit (the limit itself is in the plan's
    /// line): the client's side. `age` — their years; null — the card has no
    /// birth date; undefined — not known here, the sale checks it.
    ageNote: (age?: number | null) =>
      age === undefined
        ? 'Вік перевіримо за датою народження в картці клієнта.'
        : age === null
          ? 'У картці немає дати народження — додайте її через «Змінити», щоб продати цей тариф.'
          : `Клієнтові ${years(age)}.`,
    submit: 'Продати',
    submitting: 'Оформлюємо…',
    /// A lost answer: the press carried the attempt's key (specs/issued-passes.md
    /// §1a), so pressing again cannot sell twice — say so, or the desk hesitates.
    retrySafe: 'Немає відповіді від сервера. Натисніть «Продати» ще раз — двічі абонемент не оформиться.',
    pickClient: 'Оберіть клієнта',
    pickPlan: 'Оберіть тариф',
  },

  errors: {
    unknownPlan: 'Такого тарифу немає або він не у вашому клубі',
    unknownPass: 'Такого абонемента немає або він не у вашому клубі',
    planRetired: 'Тариф знято з продажу',
    planPeriodOver: 'Строк цього тарифу вже минув — продати його на цю дату не можна',
    notNewClient: 'Цей тариф лише для нових клієнтів, а в цього клієнта вже був абонемент',
    invalidTransition: 'Абонемент у такому стані цього не дозволяє',
    freezingNotAllowed: 'Тариф цього абонемента не дозволяє заморозку',
    freezeTooShort: 'Заморозка коротша за мінімальну',
    freezeExceedsMaxDays: 'Разом вийде більше днів заморозки, ніж дозволяє тариф',
    freezeLimitReached: 'Заморозок уже стільки, скільки дозволяє тариф',
    freezeInvalidRange: 'Кінець заморозки раніше за початок',
    freezeAlreadyOver: 'Ця заморозка вже минула — краще продовжте абонемент',
    unknownGroup: 'У цієї групи немає власних візитів',
    discountNotAllowed: 'Цей тариф продається без знижок — ціна не може бути нижчою за ціну тарифу',
    clientTooYoung: 'Клієнт молодший, ніж дозволяє цей тариф',
    clientTooOld: 'Клієнт старший, ніж дозволяє цей тариф',
    birthDateRequired: 'У тарифу є обмеження за віком, а в картці клієнта немає дати народження. Додайте її через «Змінити» в картці й продайте ще раз',
    clientsUnavailable: 'Не вдалося перевірити вік клієнта — спробуйте ще раз за хвилину',
    companyScopeRequired: 'Мережевий тариф (для всіх клубів) може створити лише роль з охопленням «Уся компанія»',
    saleOutOfScope: 'Цей тариф продається в клубі, до якого ваша роль не має доступу',
    /// The attempt's key already sold — and the desk has since changed the plan
    /// or the price (specs/issued-passes.md §1a).
    keyReused: (plan: string | null) =>
      plan
        ? `Попереднє натискання вже оформило абонемент «${plan}». Закрийте панель і перевірте абонементи клієнта, перш ніж продавати ще.`
        : 'Цю спробу продажу вже використано. Закрийте панель і почніть заново.',
  },
}

export type PassesStrings = typeof passesUk
